'use client';

import {visuallyHidden} from '@mui/utils';
import {Fragment, Suspense, use, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {
  trackHomepageLearnMoreClick,
  trackHomepageRegisterInterestClick,
  trackHomepageViewAllClick,
  trackHomepageViewItemClick,
} from '../analytics';
import {AboutSection} from '../components/about';
import {BrandQuoteSection, type BrandHeadlineVariant, type BrandQuoteCta} from '../components/brand';
import {HeroCurtain, HeroSection, type HeroMode, type HeroSize} from '../components/hero';
import {FeaturedProjectsCarousel, LatestNewsCarousel} from '../components/listing';
import {Typography} from '../components/primitives';
import {RegisterInterestCta, type RegisterInterestOpenOptions} from '../components/register-interest';
import {RevealCardSection} from '../components/reveal-card';
import {CarouselSectionSkeleton} from '../components/skeleton';
import {SpotlightSection} from '../components/spotlight';
import {EXPANSIONS_EMPTY, SPOTLIGHT_EMPTY} from '../content';
import {useLocale} from '../i18n';
import {
  mediaFromWire,
  newsCardFromWire,
  ScreenSectionType,
  SpotlightRedirectKind,
  type CountriesWireSection,
  type CountryCardView,
  type DetailedScreenSection,
  type FeaturedProjectsWireSection,
  type Media,
  type ProjectCardView,
  type WireSpotlightRedirect,
} from '../model';
import {aboutHref, countryHref, phaseHref, projectHref, projectsHref, unitHref} from '../routing';

const COUNTRIES_SECTION_NAME = 'countries';

type CardsById<T> = Record<string, T> | Promise<Record<string, T>>;

export interface GrovaHomePageProps {
  sections: DetailedScreenSection[];
  projectsById: CardsById<ProjectCardView>;
  countriesById: CardsById<CountryCardView>;
  quoteCta: BrandQuoteCta;
  // Rendered visually hidden as the page's H1 when the CMS hero carries no title.
  fallbackHeading?: string;
  headlineVariant?: BrandHeadlineVariant;
  heroMode?: HeroMode;
  heroSize?: HeroSize;
}

const SPOTLIGHT_SECTION_NAME = 'spotlight';
const ABOUT_SECTION_NAME = 'about';
const BRAND_QUOTE_SECTION_NAME = 'brand_quote';

const HREF_BUILDER_BY_KIND: Record<SpotlightRedirectKind, (locale: string, id: string) => string> = {
  [SpotlightRedirectKind.Country]: countryHref,
  [SpotlightRedirectKind.Project]: projectHref,
  [SpotlightRedirectKind.Phase]: phaseHref,
  [SpotlightRedirectKind.Unit]: unitHref,
};

function spotlightHref(locale: string, redirect: WireSpotlightRedirect | undefined): string | undefined {
  const id = redirect?.targetId;
  const build = redirect?.kind ? HREF_BUILDER_BY_KIND[redirect.kind] : undefined;
  return id && build ? build(locale, id) : undefined;
}

function spotlightRegisterInterest(redirect: WireSpotlightRedirect | undefined): RegisterInterestOpenOptions | undefined {
  const {kind, targetId, projectId} = redirect ?? {};
  switch (kind) {
    // Country targets, and sections the CMS left without a redirect.
    case undefined:
    case SpotlightRedirectKind.Country:
      return undefined;
    case SpotlightRedirectKind.Project:
      return targetId ? {projectId: targetId} : undefined;
    case SpotlightRedirectKind.Phase:
      return projectId ? {projectId, phaseId: targetId} : undefined;
    case SpotlightRedirectKind.Unit:
      return projectId ? {projectId, designTypeId: targetId} : undefined;
  }
}

function heading(name: string | undefined): string | undefined {
  return name?.length ? name : undefined;
}

interface RenderContext {
  t: (key: string) => string;
  locale: string;
  projectsById: CardsById<ProjectCardView>;
  countriesById: CardsById<CountryCardView>;
  quoteCta: BrandQuoteCta;
  headlineVariant?: BrandHeadlineVariant;
  heroMode?: HeroMode;
  heroSize?: HeroSize;
  heroFirst: boolean;
}

function useCardsById<T>(cards: CardsById<T>): Record<string, T> {
  return cards instanceof Promise ? use(cards) : cards;
}

function FeaturedProjects({
  section,
  cards,
  locale,
}: {
  section: FeaturedProjectsWireSection;
  cards: CardsById<ProjectCardView>;
  locale: string;
}) {
  const projectsById = useCardsById(cards);
  const projects = (section.featuredProjects?.projects ?? [])
    .slice()
    .sort((first, second) => first.order - second.order)
    .map(ref => projectsById[ref.sfId])
    .filter((card): card is ProjectCardView => Boolean(card));
  return (
    <FeaturedProjectsCarousel
      title={heading(section.name)}
      sectionId={section.uuid}
      projects={projects.map(project => ({
        uuid: project.uuid,
        title: project.title,
        image: project.image.src,
        location: project.location,
        href: projectHref(locale, project.uuid),
      }))}
      viewAllHref={projectsHref(locale)}
    />
  );
}

function Countries({
  section,
  cards,
  locale,
  t,
}: {
  section: CountriesWireSection;
  cards: CardsById<CountryCardView>;
  locale: string;
  t: (key: string) => string;
}) {
  const countriesById = useCardsById(cards);
  const countries = (section.countriesSection?.countries ?? [])
    .slice()
    .sort((first, second) => first.order - second.order)
    .map(ref => countriesById[ref.id])
    .filter((card): card is CountryCardView => Boolean(card));
  return (
    <RevealCardSection
      sectionName={COUNTRIES_SECTION_NAME}
      title={t('our_expansions_title')}
      subtitle={t('our_expansions_subtitle')}
      sectionId={section.uuid}
      items={countries.map(country => ({
        id: country.uuid,
        title: country.title,
        image: country.image.src,
        href: countryHref(locale, country.uuid),
      }))}
      ctaLabel={t('learn_more')}
      empty={EXPANSIONS_EMPTY}
    />
  );
}

function renderSection(section: DetailedScreenSection, ctx: RenderContext): ReactNode {
  const {t, locale} = ctx;
  switch (section.type) {
    case ScreenSectionType.Hero: {
      const media = mediaFromWire(section.hero?.webMedia ?? section.hero?.mobileMedia, section.name);
      if (!media) {
        return null;
      }
      const mobileMedia = mediaFromWire(section.hero?.mobileMedia, section.name);
      const showRegisterInterest = section.hero?.registerInterestEnabled ?? false;
      return (
        <HeroSection
          media={media}
          mobileMedia={mobileMedia}
          mode={ctx.heroMode}
          size={ctx.heroSize}
          headerOverlay={ctx.heroFirst}
          title={heading(section.name)}
          actions={
            showRegisterInterest ? (
              <RegisterInterestCta
                onClick={() => {
                  trackHomepageRegisterInterestClick();
                }}
              />
            ) : undefined
          }
        />
      );
    }
    case ScreenSectionType.Spotlight: {
      const spotlight = section.spotlight;
      const media = [spotlight?.topLeft, spotlight?.bottomLeft, spotlight?.right]
        .map(ref => mediaFromWire(ref))
        .filter((item): item is Media => Boolean(item));
      const href = spotlightHref(locale, spotlight?.redirect);
      const highlights = (spotlight?.highlights ?? [])
        .map(highlight => ({label: highlight.label ?? '', displayValue: highlight.value ?? ''}))
        .filter(highlight => Boolean(highlight.label || highlight.displayValue));
      const target = {id: spotlight?.redirect?.targetId ?? '', name: section.name ?? ''};
      const spotlightSection = {name: SPOTLIGHT_SECTION_NAME, id: section.uuid};
      return (
        <SpotlightSection
          label={spotlight?.label ?? ''}
          title={section.name ?? ''}
          description={spotlight?.description ?? ''}
          media={media}
          highlights={highlights}
          buttonText={spotlight?.buttonText}
          viewDetailsHref={href}
          empty={SPOTLIGHT_EMPTY}
          actions={
            <RegisterInterestCta
              options={spotlightRegisterInterest(spotlight?.redirect)}
              onClick={() => {
                trackHomepageRegisterInterestClick(target);
              }}
            />
          }
          onViewDetailsClick={() => {
            trackHomepageViewItemClick(target, spotlightSection);
          }}
        />
      );
    }
    case ScreenSectionType.FeaturedProjects:
      return (
        <Suspense fallback={<CarouselSectionSkeleton />}>
          <FeaturedProjects section={section} cards={ctx.projectsById} locale={locale} />
        </Suspense>
      );
    case ScreenSectionType.Countries:
      return (
        <Suspense fallback={<CarouselSectionSkeleton />}>
          <Countries section={section} cards={ctx.countriesById} locale={locale} t={t} />
        </Suspense>
      );
    case ScreenSectionType.AboutTeaser: {
      const teaser = section.aboutTeaser;
      const leftBackground = mediaFromWire(teaser?.leftBackground);
      const rightBackground = mediaFromWire(teaser?.rightBackground);
      const leftCircle = mediaFromWire(teaser?.leftCircle);
      const rightCircle = mediaFromWire(teaser?.rightCircle);
      const primary = leftBackground ?? rightBackground ?? leftCircle ?? rightCircle;
      if (!primary) {
        return null;
      }
      return (
        <AboutSection
          title={section.name ?? ''}
          description={teaser?.description ?? ''}
          cta={{label: t('learn_more'), href: aboutHref(locale)}}
          onCtaClick={() => {
            trackHomepageLearnMoreClick({name: ABOUT_SECTION_NAME, id: section.uuid});
          }}
          media={{
            leftBackground: (leftBackground ?? primary).src,
            rightBackground: (rightBackground ?? primary).src,
            leftCircle: leftCircle?.src,
            rightCircle: rightCircle?.src,
          }}
        />
      );
    }
    case ScreenSectionType.LatestNews: {
      const articles = (section.cards ?? []).map(newsCardFromWire);
      return <LatestNewsCarousel articles={articles} title={heading(section.name)} sectionId={section.uuid} />;
    }
    case ScreenSectionType.Quote: {
      const media = mediaFromWire(section.quote?.media);
      if (!media || !section.name) {
        return null;
      }
      return (
        <BrandQuoteSection
          title={section.name}
          variant={ctx.headlineVariant}
          image={media.src}
          cta={ctx.quoteCta}
          onCtaClick={() => {
            const quoteSection = {name: BRAND_QUOTE_SECTION_NAME, id: section.uuid};
            if (ctx.quoteCta.kind === 'registerInterest') {
              trackHomepageRegisterInterestClick(undefined, quoteSection);
            } else {
              trackHomepageViewAllClick(quoteSection);
            }
          }}
        />
      );
    }
    case ScreenSectionType.Overview:
    case ScreenSectionType.OverviewAndStatistics:
    case ScreenSectionType.FeatureItems:
    case ScreenSectionType.MediaBanner:
      return null;
  }
}

export function GrovaHomePage({
  sections,
  projectsById,
  countriesById,
  quoteCta,
  fallbackHeading,
  headlineVariant,
  heroMode,
  heroSize,
}: GrovaHomePageProps) {
  const {t} = useTranslation();
  const locale = useLocale();
  const heroIndex = sections.findIndex(section => section.type === ScreenSectionType.Hero);
  const ctx: RenderContext = {
    t,
    locale,
    projectsById,
    countriesById,
    quoteCta,
    headlineVariant,
    heroMode,
    heroSize,
    heroFirst: heroIndex === 0,
  };
  // The hero's own heading is the page's h1 when it has one; otherwise the site name
  // stands in, so the page never ships without one.
  const hasHeroHeading = sections.some(section => section.type === ScreenSectionType.Hero && heading(section.name));
  const fallback = !hasHeroHeading && fallbackHeading && (
    <Typography as="h1" sx={visuallyHidden}>
      {fallbackHeading}
    </Typography>
  );
  const rendered = sections.map((section, index) => <Fragment key={`${section.type}-${index}`}>{renderSection(section, ctx)}</Fragment>);
  if (heroIndex === -1) {
    return (
      <>
        {fallback}
        {rendered}
      </>
    );
  }
  return (
    <>
      {fallback}
      {rendered.slice(0, heroIndex + 1)}
      <HeroCurtain mode={heroMode}>{rendered.slice(heroIndex + 1)}</HeroCurtain>
    </>
  );
}

GrovaHomePage.displayName = 'GrovaHomePage';
